import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { ContentLayout } from 'components/Layout';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { DetailViewTabList } from 'components/TabList';
import { Search } from 'components/Search';
import { Loader } from 'components/Loader';
import { NotFound } from 'components/NotFound/NotFound';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { getCompanies } from 'features/companies/api/getAllCompanies';
import { paths } from 'routes';
import { useAuth } from 'lib/auth';

import { useCompanyApplications } from '../../api/getCompanyApplications';
import { searchJobAds } from '../../api/getAll';
import { EntityFilter } from '../../components/Applications/EntityFilter';
import { ApplicationListItem } from '../../components/Applications/ApplicationListItem';

const JobAdItem = ({ headline, ...rest }) => (
  <Stack gap={1} alignItems={'flex-start !important'} {...rest}>
    <Typography variant="body1" fontWeight="bold">
      {headline}
    </Typography>
  </Stack>
);

const CompanyItem = ({ Name, VAT, ...rest }) => (
  <Stack gap={1} alignItems={'flex-start !important'} {...rest}>
    <Typography variant="body1" fontWeight="bold">
      {Name} ({VAT})
    </Typography>
  </Stack>
);

const SUPPLIER_TABS = [
  { label: 'Received', value: 'received' },
  { label: 'Sent', value: 'sent' },
];

export const ApplicationsList = () => {
  const { account, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isSuperAdmin = account.super_admin;
  const isSupplier = !isSuperAdmin && user?.Company?.Type === 'supplier';
  const isBuyer = !isSuperAdmin && user?.Company?.Type === 'buyer';
  const hasAccess = isSuperAdmin || isSupplier || isBuyer;

  const currentTab = searchParams.get('tab') === 'sent' ? 'sent' : 'received';
  const scope = isSuperAdmin ? null : isSupplier ? currentTab : 'received';

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');
  const [jobAd, setJobAd] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [supplier, setSupplier] = useState(null);

  const {
    data: { applications, total },
    isFetching,
  } = useCompanyApplications({
    scope,
    search,
    adID: jobAd?.ad_id ?? null,
    buyerID: buyer?.company_id ?? null,
    supplierID: supplier?.company_id ?? null,
    page: page - 1,
    limit: pageSize,
    queryOptions: { enabled: hasAccess },
  });

  const debouncedSearchChange = useDebouncedCallback((val) => {
    setSearch(val);
    setPage(1);
  }, 300);
  const handleSearchChange = ({ target: { value } }) => debouncedSearchChange(value);

  const withPageReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const handleTabChange = (_, newTab) => {
    setPage(1);
    setJobAd(null);
    setSearchParams({ tab: newTab }, { replace: true });
  };

  if (!hasAccess) return null;

  return (
    <ContentLayout>
      <Stack height={1}>
        <BreadCrumbs
          items={[{ name: '', to: paths.applications }, { name: `Applications${total > 0 ? ` (${total})` : ''}` }]}
        />

        {isSupplier && (
          <>
            <Box sx={{ height: 16, backgroundColor: 'background.paper' }} />
            <DetailViewTabList value={currentTab} onChange={handleTabChange} tabs={SUPPLIER_TABS} />
          </>
        )}

        <Stack px={4} py={2} bgcolor="background.paper" borderTop="1px solid" borderColor="border.main" height="100%">
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <Search handleSearchChange={handleSearchChange} sx={{ minWidth: 200, width: 270, flexShrink: 0 }} />
            <EntityFilter
              name={`applicationsJobAdFilter-${scope}`}
              label="Job ad"
              queryFunction={(value) => searchJobAds(value, scope === 'sent' ? null : 'mine')}
              optionLabelName="headline"
              ItemCard={JobAdItem}
              value={jobAd}
              onChange={withPageReset(setJobAd)}
            />
            {isSuperAdmin && (
              <>
                <EntityFilter
                  name="applicationsBuyerFilter"
                  label="Buyer"
                  queryFunction={(value) => getCompanies('buyer', 15, true, value)}
                  optionLabelName="Name"
                  ItemCard={CompanyItem}
                  value={buyer}
                  onChange={withPageReset(setBuyer)}
                />
                <EntityFilter
                  name="applicationsSupplierFilter"
                  label="Supplier"
                  queryFunction={(value) => getCompanies('supplier', 15, true, value)}
                  optionLabelName="Name"
                  ItemCard={CompanyItem}
                  value={supplier}
                  onChange={withPageReset(setSupplier)}
                />
              </>
            )}
          </Stack>

          {isFetching && applications.length === 0 ? (
            <Loader />
          ) : (
            <>
              {applications.length === 0 ? (
                <NotFound wrapperSx={{ mt: 19 }} title="No applications found" />
              ) : (
                <>
                  <Stack
                    flex={1}
                    mt={2}
                    py={2}
                    bgcolor="background.paper"
                    borderTop="1px solid"
                    borderColor="border.main"
                    gap={1}
                  >
                    {applications.map((application) => (
                      <ApplicationListItem key={application.application_id} application={application} />
                    ))}
                  </Stack>
                  <Stack direction="row" justifyContent="center">
                    <Pagination
                      page={page}
                      handlePageChange={(_, newPage) => setPage(newPage)}
                      itemCount={total}
                      itemsPerPage={pageSize}
                      onItemsPerPageChange={setPageSize}
                    />
                  </Stack>
                </>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </ContentLayout>
  );
};
